<script lang="ts">
  import { page } from '$app/stores';

  import { get } from 'svelte/store';
  import { superForm } from 'sveltekit-superforms/client';

  import { TextField } from '@shared/ui/fields/text-field';
  import { Icon } from '@shared/ui/icon';
  import { Loader } from '@shared/ui/loader';
  import { Recaptcha, recaptchaModel } from '@shared/ui/recaptcha';
  import { cn } from '@shared/utils';

  import { snacks } from '@features/snackbar';

  import { institutionsSubscribeFormSchema } from '../model';

  const data = $page.data.form;
  const recaptchaContainerId = 'institutions-subscribe';

  export let inputClass = '';
  export let inputContainerClass = '';
  export let disclaimerClass = '';
  export let buttonClass = '';
  export let autofillBackground = true;

  const { form, errors, enhance, reset, submitting, validate } = superForm<typeof institutionsSubscribeFormSchema>(
    data,
    {
      resetForm: true,
      taintedMessage: false,
      multipleSubmits: 'allow',
      id: 'subscribe',
      validators: institutionsSubscribeFormSchema,
      onError: ({ result }) => {
        const { status } = result;

        if (status === 500) {
          snacks.push({ type: 'error', message: 'Something went wrong' });
        }
      },
      onResult: ({ result }) => {
        if (result.type === 'success') {
          snacks.push({ type: 'success', message: 'You have been successfully subscribed!' });

          reset();
        }
      },
      async onSubmit(formToSubmit) {
        const { valid } = await validate();

        const formData = get(form);

        if (!valid) {
          formToSubmit.cancel();
        }

        if (!formData.recaptchaToken) {
          const token = (await recaptchaModel.executeRecaptchaContainer(recaptchaContainerId)) as string | undefined;

          if (!token) {
            return formToSubmit.cancel();
          }

          formToSubmit.formData.set('recaptchaToken', token);
        }
      },
    },
  );

  $: getErrors = (name: string): string => {
    const err = ($errors as { [key: string]: string[] })[name];
    if (!err || !err.length) return '';
    return err[0];
  };
</script>

<form
  class="w-full"
  method="POST"
  action="/api/institutions-subscribe?institutionId={$page.data.configurations.id}"
  use:enhance
>
  <TextField
    name="email"
    inputmode="email"
    state="{$errors.email && 'error'}"
    helperText="{getErrors('email')}"
    placeholder="Email"
    bind:value="{$form.email}"
    fieldClass="{cn('text-default-50 px-0', inputClass, {
      'subscribe-input': autofillBackground,
    })}"
    containerClass="{cn('border mb-4 border-dark-400 px-4  rounded-md bg-transparent', inputContainerClass)}"
  />

  <Recaptcha
    name="recaptchaToken"
    containerId="{recaptchaContainerId}"
    error="{getErrors('recaptchaToken')}"
    bind:value="{$form.recaptchaToken}"
  />

  <p class="{cn('text-dark-700 mb-4 text-xs', disclaimerClass)}">
    By subscribing to our newsletters, you agree to our
    <a href="/privacy-policy/" class="underline-static underline-hover"> Privacy Policy </a>.
  </p>

  <button
    class="{cn(
      'bg-dark-400 group flex w-full items-center justify-center gap-5 rounded-lg py-3 text-base font-medium md:py-3',
      buttonClass,
    )}"
    type="submit"
  >
    <span> Subscribe </span>

    {#if $submitting}
      <Loader />
    {:else}
      <Icon width="{24}" height="{24}" name="arrow" classes="transition-transform group-hover:translate-x-1" />
    {/if}
  </button>
</form>

<style lang="scss">
  :global(.subscribe-input:-webkit-autofill),
  :global(.subscribe-input:-webkit-autofill:hover) {
    -webkit-text-fill-color: rgb(27, 27, 27);
    -webkit-box-shadow: 0 0 0 40rem transparent inset !important;
  }

  :global(.subscribe-input:-webkit-autofill:focus) {
    -webkit-text-fill-color: rgb(27, 27, 27);
    -webkit-box-shadow: 0 0 0 40rem transparent inset !important;
  }
</style>
